import { 
    Container, 
    Grid, 
    Card, 
    CardContent, 
    CardMedia,
    Typography,
    Avatar,
    Box,
    Chip,
    Paper,
    Link,
    Skeleton,
    Divider,
    Button,
    CircularProgress,
    LinearProgress
} from "@mui/material";
import { useEffect, useState } from "react";
import { userRequest } from "../../requestMethods";
import { useNavigate } from "react-router-dom";
import { 
    History, 
    Favorite, 
    Email, 
    BookmarkBorder, 
    MenuBookOutlined, 
    TrendingUp,
    Schedule,
    VideoLibrary,
    AutoStories,
    Analytics
} from "@mui/icons-material";
import logger from '../../services/logger';

const ProfileAvatar = ({ email, name }) => {
    const isDefaultUser = email === "shbobur2@gmail.com";
    const avatarUrl = isDefaultUser 
        ? "https://media-exp1.licdn.com/dms/image/C4D03AQFx593mEoBjXQ/profile-displayphoto-shrink_800_800/0/1654325884945?e=1674691200&v=beta&t=BvYtiM7zFuC5Aeo0CQGviHmJZF684lvy7MAWNoTHKSQ"
        : "https://www.pngfind.com/pngs/m/610-6104451_image-placeholder-png-user-profile-placeholder-image-png.png";

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
                src={avatarUrl}
                alt={name || "User Profile"}
                sx={{
                    width: 50,
                    height: 50,
                    border: 2,
                    borderColor: 'primary.main'
                }}
            />
            <Box>
                <Typography variant="h6" fontWeight="600" color="text.primary">
                    {name || "User"}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                        {email}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const HistoryItem = ({ historyEntry }) => {
    const { contentId: content, contentType, accessedAt } = historyEntry;
    
    // Generate the appropriate URL based on content type
    const getContentUrl = () => {
        switch (contentType) {
            case 'ContentShortStory':
                return `/story/${content._id}`;
            case 'ContentVideoPost':
                return `/video-posts/${content._id}`;
            case 'ContentLongPost':
                return `/long-posts/${content.slug}`;
            case 'ContentPlaylist':
                return `/playlist/${content._id}`;
            default:
                return `/story/${content._id}`;
        }
    };

    // Get content type label for display
    const getContentTypeLabel = () => {
        switch (contentType) {
            case 'ContentShortStory':
                return 'Story';
            case 'ContentVideoPost':
                return 'Video';
            case 'ContentLongPost':
                return 'Article';
            case 'ContentPlaylist':
                return 'Playlist';
            default:
                return 'Content';
        }
    };
    
    return (
        <Card 
            sx={{ 
                mb: 1.5, 
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                },
                boxShadow: 1
            }}
        >
            <Link 
                href={getContentUrl()} 
                sx={{ textDecoration: 'none', color: 'inherit' }}
            >
                <Box sx={{ display: 'flex' }}>
                    <CardMedia
                        component="img"
                        sx={{ 
                            width: { xs: 60, sm: 80 },
                            height: { xs: 60, sm: 80 },
                            objectFit: 'cover'
                        }}
                        image={content.thumbUrl}
                        alt={content.name || content.title}
                    />
                    <CardContent sx={{ flex: 1, py: { xs: 1, sm: 1.5 }, px: { xs: 1.5, sm: 2 } }}>
                        <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ lineHeight: 1.3 }}>
                            {content.name || content.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip 
                                label={getContentTypeLabel()} 
                                size="small" 
                                variant="outlined"
                                sx={{ 
                                    height: 20, 
                                    fontSize: '0.7rem',
                                    '& .MuiChip-label': { px: 1 }
                                }}
                            />
                            {content.author && (
                                <Typography variant="caption" color="text.secondary">
                                    by {content.author}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <History color="action" fontSize="small" />
                            <Typography variant="caption" color="text.secondary">
                                {new Date(accessedAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </Typography>
                        </Box>
                    </CardContent>
                </Box>
            </Link>
        </Card>
    );
};

const FavoriteItem = ({ favoriteEntry }) => {
    const { contentId: content, contentType, addedAt } = favoriteEntry;
    
    return (
        <Card 
            sx={{ 
                mb: 1.5,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                },
                boxShadow: 1
            }}
        >
            <Link 
                href={`/story/${content._id}`} 
                sx={{ textDecoration: 'none', color: 'inherit' }}
            >
                <Box sx={{ display: 'flex' }}>
                    <CardMedia
                        component="img"
                        sx={{ 
                            width: { xs: 60, sm: 80 },
                            height: { xs: 60, sm: 80 },
                            objectFit: 'cover'
                        }}
                        image={content.thumbUrl}
                        alt={content.name || content.title}
                    />
                    <CardContent sx={{ flex: 1, py: { xs: 1, sm: 1.5 }, px: { xs: 1.5, sm: 2 } }}>
                        <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ lineHeight: 1.3 }}>
                            {content.name || content.title}
                        </Typography>
                    </CardContent>
                </Box>
            </Link>
        </Card>
    );
};

const SectionSkeleton = () => (
    <Box sx={{ mb: 3 }}>
        <Skeleton variant="text" width="30%" height={40} sx={{ mb: 2 }} />
        {[1, 2, 3].map((item) => (
            <Card key={item} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex' }}>
                    <Skeleton variant="rectangular" width={80} height={80} />
                    <CardContent sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={30} />
                        <Skeleton variant="text" width="40%" height={20} />
                    </CardContent>
                </Box>
            </Card>
        ))}
    </Box>
);

const ProgressTrackCard = ({ track }) => {
    const navigate = useNavigate();

    const handleStartFlashcards = (e) => {
        e.stopPropagation();
        navigate(`/flashcards/${track.shortStory._id}`);
    };

    return (
        <Card 
            sx={{ 
                mb: 1.5,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                },
                boxShadow: 1
            }}
        >
            {/* Story info row */}
            <Box sx={{ display: 'flex', p: { xs: 1.5, sm: 2 }, pb: { xs: 1, sm: 1 } }}>
                <CardMedia
                    component="img"
                    sx={{ 
                        width: { xs: 50, sm: 60 },
                        height: { xs: 50, sm: 60 },
                        objectFit: 'cover',
                        cursor: 'pointer',
                        borderRadius: 1
                    }}
                    image={track.shortStory.thumbUrl}
                    alt={track.shortStory.title}
                    onClick={() => navigate(`/story/${track.shortStory._id}`)}
                />
                <Box sx={{ ml: { xs: 1.5, sm: 2 }, flex: 1 }}>
                    <Typography 
                        variant="subtitle2" 
                        fontWeight="600" 
                        sx={{ 
                            cursor: 'pointer',
                            lineHeight: 1.3,
                            mb: 0.5,
                            '&:hover': { color: 'primary.main' }
                        }}
                        onClick={() => navigate(`/story/${track.shortStory._id}`)}
                    >
                        {track.shortStory.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {track.totalWordsNum} words learned
                    </Typography>
                </Box>
            </Box>

            {/* Words and phrases section */}
            <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.8, sm: 1 }, mb: { xs: 1.5, sm: 2 } }}>
                    {/* Words */}
                    {track.words.slice(0, 4).map((word) => (
                        <Chip
                            key={word.id}
                            label={word.w}
                            variant="filled"
                            sx={{ 
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                fontWeight: '600',
                                height: { xs: 30, sm: 32 },
                                backgroundColor: '#e3f2fd',
                                color: '#1565c0',
                                '& .MuiChip-label': {
                                    px: { xs: 1.5, sm: 2 }
                                }
                            }}
                        />
                    ))}
                    {/* Phrases */}
                    {track.phrases.slice(0, 4).map((phrase) => (
                        <Chip
                            key={phrase.id}
                            label={phrase.w}
                            variant="filled"
                            color="secondary"
                            size="medium"
                            sx={{ 
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                fontWeight: '600',
                                height: { xs: 30, sm: 32 },
                                '& .MuiChip-label': {
                                    px: { xs: 1.5, sm: 2 }
                                }
                            }}
                        />
                    ))}
                    {track.totalWordsNum > 8 && (
                        <Chip 
                            label={`+${track.totalWordsNum - 8}`}
                            size="medium"
                            variant="outlined"
                            color="info"
                            sx={{ 
                                fontSize: { xs: '0.8rem', sm: '0.85rem' }, 
                                height: { xs: 30, sm: 32 },
                                fontWeight: '600'
                            }}
                        />
                    )}
                </Box>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<MenuBookOutlined />}
                    onClick={handleStartFlashcards}
                    fullWidth
                    sx={{ 
                        textTransform: 'none',
                        borderRadius: 2,
                        py: 1,
                        fontWeight: '600',
                        backgroundColor: '#4caf50',
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: '#388e3c'
                        }
                    }}
                >
                    Check your dictionary
                </Button>
            </Box>
        </Card>
    );
};

// Dashboard Stats Component
const DashboardStats = ({ totalWords, totalHistory, totalFavorites }) => {
    const stats = [
        {
            title: 'Words Learned',
            value: totalWords,
            icon: <BookmarkBorder />,
            color: 'primary',
            trend: '+12 this week'
        },
        {
            title: 'Stories Read',
            value: totalHistory,
            icon: <AutoStories />,
            color: 'secondary',
            trend: '+3 this week'
        },
        {
            title: 'Favorites',
            value: totalFavorites,
            icon: <Favorite />,
            color: 'error',
            trend: '+2 this week'
        },
        {
            title: 'Study Streak',
            value: '7 days',
            icon: <TrendingUp />,
            color: 'success',
            trend: 'Keep it up!'
        }
    ];

    return (
        <Grid container spacing={3} sx={{ mb: 4 }}>
            {stats.map((stat, index) => (
                <Grid item xs={6} md={3} key={index}>
                    <Card 
                        sx={{ 
                            height: '100%',
                            background: `linear-gradient(135deg, ${stat.color === 'primary' ? '#1976d2' : 
                                stat.color === 'secondary' ? '#dc004e' : 
                                stat.color === 'error' ? '#d32f2f' : '#2e7d32'} 0%, ${
                                stat.color === 'primary' ? '#42a5f5' : 
                                stat.color === 'secondary' ? '#f48fb1' : 
                                stat.color === 'error' ? '#f44336' : '#66bb6a'} 100%)`,
                            color: 'white'
                        }}
                    >
                        <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                        {stat.title}
                                    </Typography>
                                    <Typography variant="h4" fontWeight="bold">
                                        {stat.value}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                        {stat.trend}
                                    </Typography>
                                </Box>
                                <Box sx={{ opacity: 0.8 }}>
                                    {stat.icon}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

// Simple chart component using progress bars
const WeeklyProgressChart = ({ totalWords }) => {
    // Mock data for weekly progress
    const weeklyData = [
        { day: 'Mon', words: Math.floor(totalWords * 0.15), reading: 45 },
        { day: 'Tue', words: Math.floor(totalWords * 0.12), reading: 30 },
        { day: 'Wed', words: Math.floor(totalWords * 0.18), reading: 60 },
        { day: 'Thu', words: Math.floor(totalWords * 0.10), reading: 25 },
        { day: 'Fri', words: Math.floor(totalWords * 0.20), reading: 75 },
        { day: 'Sat', words: Math.floor(totalWords * 0.16), reading: 50 },
        { day: 'Sun', words: Math.floor(totalWords * 0.09), reading: 20 }
    ];

    const maxWords = Math.max(...weeklyData.map(d => d.words));
    const maxReading = Math.max(...weeklyData.map(d => d.reading));

    return (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2, position: 'relative' }}>
            {/* Mock indicator */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    color: 'red',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    transform: 'rotate(45deg)',
                    transformOrigin: 'center',
                    opacity: 0.7,
                    userSelect: 'none',
                    pointerEvents: 'none'
                }}
            >
                MOCK
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Analytics sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="600">
                    This Week's Progress
                </Typography>
            </Box>
            
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                        Words Learned Daily
                    </Typography>
                    {weeklyData.map((day) => (
                        <Box key={day.day} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2">{day.day}</Typography>
                                <Typography variant="body2" fontWeight="600">{day.words}</Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={(day.words / maxWords) * 100} 
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>
                    ))}
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom color="secondary">
                        Reading Time (minutes)
                    </Typography>
                    {weeklyData.map((day) => (
                        <Box key={day.day} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2">{day.day}</Typography>
                                <Typography variant="body2" fontWeight="600">{day.reading}m</Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={(day.reading / maxReading) * 100} 
                                color="secondary"
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>
                    ))}
                </Grid>
            </Grid>
        </Paper>
    );
};

const Profile = () => {
    const [userData, setUserData] = useState(null);
    const [progressTracks, setProgressTracks] = useState([]);
    const [favorites, setFavorites] = useState([]);
    const [history, setHistory] = useState([]);
    const [profileLoading, setProfileLoading] = useState(true);
    const [progressLoading, setProgressLoading] = useState(false);
    const [favoritesLoading, setFavoritesLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [progressPage, setProgressPage] = useState(1);
    const [favoritesPage, setFavoritesPage] = useState(2);
    const [historyPage, setHistoryPage] = useState(2);
    const [progressHasMore, setProgressHasMore] = useState(false);
    const [favoritesHasMore, setFavoritesHasMore] = useState(false);
    const [historyHasMore, setHistoryHasMore] = useState(false);
    const [totalLearnedWordsNum, setTotalLearnedWordsNum] = useState(0);
    const navigate = useNavigate();
   
    const fetchProfile = async () => {
        try {
            setProfileLoading(true);
            const res = await userRequest(`/user/profile`);
            const profileData = res.data;
            setUserData(profileData);
            
            // Set initial data from profile response
            setFavorites(profileData.favorites || []);
            setHistory(profileData.history || []);
            setFavoritesHasMore(profileData.favoritesHasMore || false);
            setHistoryHasMore(profileData.historyHasMore || false);
            
            logger.info('Profile data loaded successfully', {
                hasHistory: !!profileData.history?.length,
                hasFavorites: !!profileData.favorites?.length,
                userId: profileData._id
            });
        } catch (error) {
            logger.error('Failed to fetch user profile data', {
                error: error.message,
                status: error.response?.status
            });
        } finally {
            setProfileLoading(false);
        }
    };

    const fetchProgressTracks = async (pageNum = 1) => {
        try {
            setProgressLoading(true);
            const res = await userRequest.get(`/progress-tracks?page=${pageNum}&limit=5`);
            if (pageNum === 1) {
                setProgressTracks(res.data.progressTracks);
                setTotalLearnedWordsNum(res.data.totalLearnedWordsNum || 0);
            } else {
                setProgressTracks(prev => [...prev, ...res.data.progressTracks]);
            }
            setProgressHasMore(res.data.hasMore);
            setProgressPage(pageNum);
        } catch (error) {
            console.error('Error fetching progress tracks:', error);
        } finally {
            setProgressLoading(false);
        }
    };

    const fetchFavorites = async (pageNum = 2) => {
        try {
            setFavoritesLoading(true);
            const res = await userRequest.get(`/user/favorites?page=${pageNum}&limit=5`);
            setFavorites(prev => [...prev, ...res.data.favorites]);
            setFavoritesHasMore(res.data.hasMore);
            setFavoritesPage(pageNum);
        } catch (error) {
            console.error('Error fetching favorites:', error);
        } finally {
            setFavoritesLoading(false);
        }
    };

    const fetchHistory = async (pageNum = 2) => {
        try {
            setHistoryLoading(true);
            const res = await userRequest.get(`/user/history?page=${pageNum}&limit=5`);
            setHistory(prev => [...prev, ...res.data.history]);
            setHistoryHasMore(res.data.hasMore);
            setHistoryPage(pageNum);
        } catch (error) {
            console.error('Error fetching history:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleLoadMoreProgress = () => {
        fetchProgressTracks(progressPage + 1);
    };

    const handleLoadMoreFavorites = () => {
        fetchFavorites(favoritesPage + 1);
    };

    const handleLoadMoreHistory = () => {
        fetchHistory(historyPage + 1);
    };


    useEffect(() => {
        fetchProfile(); // This will set initial favorites and history
        fetchProgressTracks(); // Only progress tracks need separate initial fetch
    }, []);

    if (profileLoading) {
        return (
            <Container maxWidth="md" sx={{ py: 2 }}>
                <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="circular" width={50} height={50} />
                        <Box>
                            <Skeleton variant="text" width={200} height={25} />
                            <Skeleton variant="text" width={150} height={20} />
                        </Box>
                    </Box>
                </Paper>
                <SectionSkeleton />
                <SectionSkeleton />
                <SectionSkeleton />
            </Container>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh' }}>
            <Container maxWidth="lg" sx={{ py: 3 }}>
                {/* Welcome Header */}
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Typography 
                        variant="h3" 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 600,
                            color: 'primary.main',
                            mb: 1
                        }}
                    >
                        Welcome back, {userData?.name || 'User'}!
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Here's your learning progress overview
                    </Typography>
                </Box>

                {/* Dashboard Stats */}
                {userData && (
                    <DashboardStats 
                        totalWords={totalLearnedWordsNum}
                        totalHistory={history.length}
                        totalFavorites={favorites.length}
                    />
                )}

                {/* Weekly Progress Chart */}
                {!profileLoading && (
                    <Box sx={{ mb: 4 }}>
                        <WeeklyProgressChart totalWords={totalLearnedWordsNum} />
                    </Box>
                )}

            {/* Vertical Layout: New Words → Favorites → History */}
            {userData && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* New Words & Phrases Section - Top */}
                    <Paper elevation={1} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <BookmarkBorder sx={{ mr: 1, color: 'info.main' }} />
                            <Typography variant="h6" fontWeight="600" color="info.main">
                                New Words & Phrases
                            </Typography>
                            <Chip 
                                label={totalLearnedWordsNum} 
                                size="small" 
                                color="info" 
                                variant="outlined"
                                sx={{ ml: 2 }}
                            />
                        </Box>
                        
                        <Divider sx={{ mb: 2 }} />
                        
                        {progressLoading && progressPage === 1 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CircularProgress size={40} />
                            </Box>
                        ) : progressTracks.length > 0 ? (
                            <>
                                <Box 
                                    sx={{ 
                                        display: 'grid',
                                        gridTemplateColumns: { 
                                            xs: '1fr', 
                                            md: 'repeat(2, 1fr)' 
                                        },
                                        gap: { xs: 0, md: 2 }
                                    }}
                                >
                                    {progressTracks.map((track) => (
                                        <ProgressTrackCard 
                                            key={track._id} 
                                            track={track}
                                        />
                                    ))}
                                </Box>
                                {progressHasMore && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Button 
                                            variant="outlined" 
                                            onClick={handleLoadMoreProgress}
                                            disabled={progressLoading}
                                            size="small"
                                        >
                                            {progressLoading ? <CircularProgress size={20} /> : 'Load More'}
                                        </Button>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <BookmarkBorder color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                    No words or phrases yet
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Click on words while reading to track them here
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    {/* Favorites Section - Middle */}
                    {/* <Paper elevation={1} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Favorite sx={{ mr: 1, color: 'secondary.main' }} />
                            <Typography variant="h6" fontWeight="600" color="secondary.main">
                                Favorites
                            </Typography>
                            <Chip 
                                label={favorites.length} 
                                size="small" 
                                color="secondary" 
                                variant="outlined"
                                sx={{ ml: 2 }}
                            />
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        {profileLoading ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CircularProgress size={40} />
                            </Box>
                        ) : favorites.length > 0 ? (
                            <>
                                {favorites.map((item, index) => (
                                    <FavoriteItem 
                                        key={`${item.contentType}-${item.contentId._id}-${index}`} 
                                        favoriteEntry={item} 
                                    />
                                ))}
                                {favoritesHasMore && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Button 
                                            variant="outlined" 
                                            onClick={handleLoadMoreFavorites}
                                            disabled={favoritesLoading}
                                            size="small"
                                        >
                                            {favoritesLoading ? <CircularProgress size={20} /> : 'Load More'}
                                        </Button>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <Favorite color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                    No favorites yet
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Mark stories as favorite to see them here
                                </Typography>
                            </Box>
                        )}
                    </Paper> */}

                    {/* History Section - Bottom */}
                    <Paper elevation={1} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <History sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="h6" fontWeight="600" color="primary.main">
                                Recent History
                            </Typography>
                            <Chip 
                                label={history.length} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                                sx={{ ml: 2 }}
                            />
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        {profileLoading ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CircularProgress size={40} />
                            </Box>
                        ) : history.length > 0 ? (
                            <>
                                {history.map((item, index) => (
                                    <HistoryItem 
                                        key={`${item.contentType}-${item.contentId._id}-${index}`} 
                                        historyEntry={item} 
                                    />
                                ))}
                                {historyHasMore && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Button 
                                            variant="outlined" 
                                            onClick={handleLoadMoreHistory}
                                            disabled={historyLoading}
                                            size="small"
                                        >
                                            {historyLoading ? <CircularProgress size={20} /> : 'Load More'}
                                        </Button>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <History color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                    No reading history yet
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Start reading to see your history here
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Box>
            )}
            </Container>
        </Box>
    );
};

export default Profile;